import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { staticRoutes } from 'Deer/constants/routes';
import { useCustomNameGoalsCompanyTabs } from 'Deer/hooks/useCustomNameGoalsCompanyTabs';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { useViewFromQueryParam } from 'Deer/hooks/useViewFromQueryParam';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { isEmpty } from 'lodash';
import TeamModel from 'models/team';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { INSIGHT_VIEWS, useOKROwnership } from 'reducers/okrsPageSlice';
import { setSelectedView, useSelectedView } from 'reducers/viewsSlice';
import { getCompletedOnDurationFromFilters } from 'screens/Initiatives/helpers';
import { getDefaultLocalStorageStatsFilters, useTeamPageState } from 'screens/Team';
import { GroupByGoalMetricsCards } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/MetricsCards/GroupByGoal/GroupByGoalMetricsCards';
import { OKRSidePanels } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/OKRSidePanels';
import { goalsCascadedViewReactQueryKey } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import { useGroupByGoalsData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useGroupByGoalsData';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import Header from 'screens/Team/Rabbit/Goals/Header';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
import { BulkUploadStatus } from 'screens/admin/Employees/BulkUploadSidePanel/components/BulkUploadStatus';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';
import { okrsPageSelector } from 'selectors/okrsPage';
import { baseTheme } from 'themes/clients/baseTheme';
import { checkIntentLandingPagesFeatureEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { TASK_TYPES_2 } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import {
  sendCompanyBulkCheckInPageViewEvent,
  sendDirectTeamBulkCheckInPageViewEvent,
  sendMeBulkCheckInPageViewEvent,
  sendSkipTeamBulkCheckInPageViewEvent,
} from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';

import { Layout } from '../components/Layout/Layout';
import CheckInErrorScreen from './components/CheckInErrorScreen';
import { CheckInGoalsTable } from './components/CheckInGoalsTable';
import CheckInLoadingScreen from './components/CheckInLoadingScreen';
import CheckInSuccessScreen from './components/CheckInSuccessScreen';
import { IntentDropdown } from './components/IntentDropdown';
import { useGoToMangeGoals } from './hooks/useGoToMangeGoals';
import { useHandleUnsavedDataCheckIn } from './hooks/useHandleUnsavedDataCheckIn';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const generateAttributeFilter = (activeFilters = []) => {
  const data = activeFilters.map(({ value }) => value);
  return {
    type: 'STATUS',
    operatorType: 'IN',
    data,
  };
};

type UploadStatus = {
  totalRecords?: number;
  recordsUpdated?: number;
  status: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED';
  uuid?: string;
  fileName?: string;
};

const BulkCheckInLoadingScreen = ({
  uploadStatus,
  setShowLoadingScreen,
  goToManageGoals,
}: {
  uploadStatus: UploadStatus;
  setShowLoadingScreen: (value: boolean) => void;
  goToManageGoals: () => void;
}) => {
  if (uploadStatus?.status === 'VALIDATING' || uploadStatus?.status === 'IN_PROGRESS') {
    return (
      <CheckInLoadingScreen totalRecords={uploadStatus?.totalRecords} recordsUpdated={uploadStatus?.recordsUpdated} />
    );
  }
  if (uploadStatus?.status === 'ERROR') {
    return (
      <CheckInErrorScreen
        onClickGoToCheckIn={setShowLoadingScreen}
        uuid={uploadStatus?.uuid}
        fileName={uploadStatus?.fileName}
      />
    );
  }

  if (uploadStatus?.status === 'FINISHED') {
    return <CheckInSuccessScreen onClickGoToCheckIn={setShowLoadingScreen} onClickGoToGoals={goToManageGoals} />;
  }

  return null;
};

const CheckInIntentTable = ({ showMetrics = true, gridRef, showSuccessorErrorScreen, setShowLoadingScreen }) => {
  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);
  const [refetchUploadStatusToggle, setRefetchUploadStatusToggle] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    totalRecords?: number;
    recordsUpdated?: number;
    status?: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED';
    uuid?: string;
  }>({});

  const firstStatusRef = useRef(true);

  useEffect(() => {
    if (isEmpty(uploadStatus)) {
      setShowLoadingScreen(false);
    } else if (firstStatusRef.current) {
      firstStatusRef.current = false;

      if (uploadStatus?.status === 'IN_PROGRESS' || uploadStatus?.status === 'VALIDATING') {
        setShowLoadingScreen(true);
      }
    } else {
      setShowLoadingScreen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadStatus]);

  const location = useLocation();

  const {
    cardKPIData: groupByGoalCardKPIData,
    isLoading: groupByGoalIsLoading,
    setChartData: setGroupByGoalChartData,
    onModalUpdated: onGroupByGoalModalUpdated,
    setRowData: groupByGoalSetRowData,
  } = useGroupByGoalsData({
    gridRef,
  });

  /*  read keyResultsName and objectivesName from new config */

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};
  const dispatch = useDispatch();

  const { viewType: groupByType } = useSelector(okrsInsightsSelector);

  const isGroupByGoals = groupByType === INSIGHT_VIEWS[1].value;

  const { team, setTeam, activeFilters, selectedTaskType, activeFiltersDispatch, newFiltersState } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const attributeFilter = generateAttributeFilter(activeFilters);

  const [, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));

  const { viewType: OKRManageView, replaceViewType, setViewType } = useOKRManageViewType();

  const isHierarchyView = OKRManageView === 'listWithHierarchy';

  const goToManageGoals = useGoToMangeGoals();

  const ownership = useOKROwnership();

  useEffect(() => {
    if (!OKRManageView) {
      replaceViewType('list');
    }
  }, [OKRManageView, replaceViewType]);

  useEffect(() => {
    // when sideNavSelectedCohort changes, reset the view type to list
    if (OKRManageView !== 'list' && !checkNewNavEnabled()) {
      setViewType('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavSelectedCohort?.id, checkNewNavEnabled]);

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        pageType: 'TEAM_HUB',
      })
    );
  };

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(newFiltersState)]);

  if (!sideNavSelectedCohort || !team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  const showParentObjectives = Boolean(
    matchPath(location.pathname, {
      path: routes.goals.audit,
      exact: false,
    })
  );

  const showChartsAndMetrics = OKRManageView === 'list' || OKRManageView === 'listWithHierarchy';

  return (
    <>
      {
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={showSuccessorErrorScreen ? 'calc(100vh - 162px)' : 'auto'}
          overflow="auto"
          background="white"
          p={showSuccessorErrorScreen ? 3 : 0}
          width="100%"
          borderRadius="8px"
          boxShadow={baseTheme.shadows.paper}
        >
          <BulkUploadStatus
            // @ts-expect-error : wrong type
            getUploadStatus={(curStatus: { status: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED' }) => {
              if (curStatus?.status !== uploadStatus?.status) {
                setUploadStatus(curStatus);
              }
            }}
            reload={refetchUploadStatusToggle}
            renderUI={(status: UploadStatus) => {
              if (showSuccessorErrorScreen === false) {
                return null;
              }

              return (
                <BulkCheckInLoadingScreen
                  uploadStatus={status}
                  goToManageGoals={goToManageGoals}
                  setShowLoadingScreen={setShowLoadingScreen}
                />
              );
            }}
            uploadType="GOAL_BULK_CHECKIN"
          />
        </Box>
      }

      {!showSuccessorErrorScreen && isGroupByGoals && showChartsAndMetrics ? (
        <Box mt={3}>
          <GroupByGoalMetricsCards
            cardKPIData={groupByGoalCardKPIData}
            isLoading={groupByGoalIsLoading}
            showMetrics={showMetrics}
          />
        </Box>
      ) : null}

      <Box width="100%" mt={3}>
        {!showSuccessorErrorScreen && (
          <Box background="white" width="100%" borderRadius="8px" boxShadow={baseTheme.shadows.paper}>
            <CheckInGoalsTable
              showParentObjectives={showParentObjectives}
              attributeFilter={attributeFilter}
              timeBoundPeriod={selectedTimelines?.[0] ?? null}
              type={selectedTaskType}
              isHierarchyView={isHierarchyView}
              gridRef={gridRef}
              setChartData={setGroupByGoalChartData}
              setRowData={groupByGoalSetRowData}
              onModalUpdated={onGroupByGoalModalUpdated}
              setShowLoadingScreen={setShowLoadingScreen}
              setRefetchUploadStatusToggle={setRefetchUploadStatusToggle}
              uploadStatus={uploadStatus}
              selectedTimelines={selectedTimelines}
              ownership={ownership}
            />
          </Box>
        )}
      </Box>

      <OKRSidePanels
        isOpenCreateGoalPanel={isOpenCreateGoalPanel}
        setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
        setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
        selectedTimelines={selectedTimelines}
        afterGoalCreation={refetchCascadedViewData}
      />
    </>
  );
};

export const COHORT_ID_NAME_MAP = {
  '-12': 'Me',
  '-2': 'Team',
  '-3': 'Team',
  '-1': 'Company',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CheckInGoalsIntentLandingPage = ({ cohort, headerLabel }: { cohort: any; headerLabel: string }) => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const { l3Options, l1Module, teamType } = useRouteToNavLevels();
  const { moduleName } = useOrganisationSettings();

  const goalModuleNameTabs = useMemo(
    () =>
      l3Options.map((tab) => ({
        ...tab,
        label: `${tab.label} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
      })),
    [l3Options, moduleName]
  );

  const isMyDataRoute = l1Module === 'myData';
  const isTeamRoute = l1Module === 'team';
  const isCompanyRoute = l1Module === 'company';

  const modifiedHeaderLabel = `${headerLabel} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`;
  const [showSuccessorErrorScreen, setShowLoadingScreen] = useState(false);

  const { l3Options: teamTabs } = useRouteToMyTeamLevels();

  const { tabs: companyTabs } = useCustomNameGoalsCompanyTabs();

  let tabs = [];

  if (isMyDataRoute) {
    tabs = goalModuleNameTabs;
  } else if (isTeamRoute) {
    tabs = teamTabs;
  } else if (isCompanyRoute) {
    tabs = companyTabs;
  }

  useEffect(() => {
    if (isMyDataRoute) {
      sendMeBulkCheckInPageViewEvent();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendDirectTeamBulkCheckInPageViewEvent();
      } else if (teamType === 'allReports') {
        sendSkipTeamBulkCheckInPageViewEvent();
      }
    } else if (isCompanyRoute) {
      sendCompanyBulkCheckInPageViewEvent();
    }
  }, [isCompanyRoute, isMyDataRoute, isTeamRoute, teamType]);

  const { handleUnsavedDataAlert } = useHandleUnsavedDataCheckIn();

  const { viewId, data: view, isLoading, isError } = useViewFromQueryParam();
  const history = useHistory();

  useEffect(() => {
    if (viewId) {
      if (!isLoading) {
        const cohortToSet = {
          id: view?.id,
          name: view?.name ?? '',
        };
        dispatch(setSelectedView(cohortToSet));
      }
    } else {
      dispatch(setSelectedView({ ...cohort, teamId: cohort.id }));
    }
    return () => {
      dispatch(setSelectedView({}));
    };
  }, [cohort, dispatch, isLoading, view, viewId]);

  if (isError) {
    history.replace(staticRoutes.home);
  }

  if (checkIntentLandingPagesFeatureEnabled()) {
    return (
      <Layout
        Filters={
          <IntentDropdown
            gridRef={gridRef}
            timelineDropdownOnChangeCallback={handleUnsavedDataAlert}
            ownershipDropdownOnChangeCallback={handleUnsavedDataAlert}
            breadcrumbs={{
              items: [
                {
                  label: viewId ? view?.name : COHORT_ID_NAME_MAP[cohort.id] || 'Me',
                  to: '',
                },
              ],
              page: {
                label: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
                to: '',
              },
            }}
            disableMetaFilters={showSuccessorErrorScreen}
            disableEllipsisMenu={showSuccessorErrorScreen}
          />
        }
      >
        <CheckInIntentTable
          gridRef={gridRef}
          showSuccessorErrorScreen={showSuccessorErrorScreen}
          setShowLoadingScreen={setShowLoadingScreen}
        />
      </Layout>
    );
  }

  return (
    <>
      <Header teamName={modifiedHeaderLabel} />
      <PageTabbedNavigationV1 tabs={tabs} />
      <CheckInIntentTable
        gridRef={gridRef}
        showSuccessorErrorScreen={showSuccessorErrorScreen}
        setShowLoadingScreen={setShowLoadingScreen}
      />
    </>
  );
};

export { CheckInGoalsIntentLandingPage };
